import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { AccordianItem } from "./constants";

const FAQ = ({ accordianList }: { accordianList: AccordianItem[] }) => {
  const [activeIndex, setActiveIndex] = useState<number>(1);

  return (
    <section className="px-8 font-montserrat">
      <div className="text-center text-[18px] font-medium md:text-[20.4px] lg:text-[27.2px] xl:text-[34px] 2xl:text-[40.8px]">
        Frequently Asked <span className="text-[#52A1FF]">Questions</span>
      </div>
      <div className="mt-10 flex flex-col gap-8 lg:flex-row">
        <div className="flex w-full flex-col gap-5 lg:w-[60%]">
          {accordianList.map((item) => (
            <Accordian
              key={item.id}
              activeIndex={activeIndex}
              question={item.ques}
              answer={item.ans}
              questionId={item.id}
              setActiveIndex={setActiveIndex}
            />
          ))}
        </div>
        <div className="h-fit w-full rounded-[20px] bg-white px-5 py-5 drop-shadow-md md:px-8 lg:w-[40%]">
          <h3 className="mb-5 text-lg font-semibold md:text-xl lg:text-2xl">
            <span className="border-b border-black">How</span> can we help or
            improve?
          </h3>
          <ContactForm />
        </div>
      </div>
    </section>
  );
};

export default FAQ;

const Accordian = ({
  activeIndex,
  questionId,
  question,
  answer,
  setActiveIndex,
}: {
  activeIndex: number;
  questionId: number;
  question: string;
  answer: string;
  setActiveIndex: (index: number) => void;
}) => {
  const isAccordianActive = activeIndex === questionId;
  return (
    <div
      className={`${
        isAccordianActive ? "border border-[#E3E3E3]" : "drop-shadow-md"
      } rounded-[20px] bg-white p-3 md:p-5`}
    >
      <div
        className={`inline-flex w-full items-center justify-between text-sm text-[#333333] md:text-xl lg:text-2xl ${
          !isAccordianActive ? "font-medium text-[#B2AAAA]" : "font-semibold"
        }`}
      >
        {question}
        <span
          className="flex h-4 w-4 cursor-pointer items-center rounded-full bg-[#464EF8] p-[2px] text-white md:h-6 md:w-6"
          onClick={() => setActiveIndex(questionId)}
        >
          {activeIndex === questionId ? <MinusIcon /> : <PlusIcon />}
        </span>
      </div>
      <div
        className={`mt-5 w-[70%] text-xs text-[#666666] md:text-lg lg:text-xl ${
          !isAccordianActive && "hidden"
        }`}
      >
        {answer}
      </div>
    </div>
  );
};

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFocus = (field: string) => {
    setFocusedField(field);
  };

  const handleBlur = () => {
    setFocusedField(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch("/api/sendEmail", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.log("data...", data);
      toast.success("Your message has been sent successfully.");
      setFormData({
        name: "",
        email: "",
        message: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
      console.error(err);
    }
    console.log(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div>
        <label
          htmlFor="name"
          className={`block text-xs font-medium md:text-sm ${
            focusedField === "name" ? "text-black" : "text-[#969696]"
          }`}
        >
          Your Name
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          onFocus={() => handleFocus("name")}
          onBlur={handleBlur}
          className="mt-1 block w-full rounded-3xl border border-gray-400 p-3 shadow-sm focus:border-black focus:outline-none focus:ring-indigo-500 sm:text-sm"
          required
        />
      </div>
      <div>
        <label
          htmlFor="email"
          className={`block text-xs font-medium md:text-sm ${
            focusedField === "email" ? "text-black" : "text-[#969696]"
          }`}
        >
          Your Email
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          onFocus={() => handleFocus("email")}
          onBlur={handleBlur}
          className="mt-1 block w-full rounded-3xl border border-gray-300 p-3 shadow-sm focus:border-black focus:outline-none sm:text-sm"
          required
        />
      </div>
      <div>
        <label
          htmlFor="message"
          className={`block text-xs font-medium md:text-sm ${
            focusedField === "message" ? "text-black" : "text-[#969696]"
          }`}
        >
          Your Question or Suggestion
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          onFocus={() => handleFocus("message")}
          onBlur={handleBlur}
          className="mt-1 block w-full rounded-3xl border border-gray-300 p-3 shadow-sm focus:border-black focus:outline-none sm:text-sm"
          rows={4}
          required
        />
      </div>
      <button
        type="submit"
        className="self-end rounded-3xl border border-blue-500 px-4 py-2 text-xs font-semibold text-blue-500 hover:bg-blue-600  hover:text-white md:text-sm"
      >
        Submit
      </button>
    </form>
  );
};
